/**
 * resolveAreaErrorMessage — turns a work-security AREA_* error response (see
 * PR-2 spec "Контракт бэкенда") into a human-readable string instead of the
 * raw code. Every create/edit form in the module that can touch an
 * instruction sphere runs its catch block through this first, falling back
 * to its own generic message when it returns null (not an area error).
 *
 * `update-role.js`'s Master-only area-change gate is a special case: it
 * responds `res.badRequest('COMPANY_USER_MASTER_NOT_FOUND')` — a bare string
 * body, not `{ message, code }` like every other guard in the module — so it
 * is matched separately before the `{ code }` switch below.
 */
export interface AreaErrorResponseData {
  code?: string;
  message?: string;
  /** Present only on AREA_MOVE_HAS_TEST_LINKS. */
  tests?: Array<{ id: string; name: string }>;
}

export function resolveAreaErrorMessage(err: unknown, t: any): string | null {
  const data = (err as { response?: { data?: unknown } })?.response?.data;

  if (data === "COMPANY_USER_MASTER_NOT_FOUND") {
    return t("area_error_master_required", {
      defaultValue: "Only Master can move this to another sphere.",
    });
  }

  if (!data || typeof data !== "object") {
    return null;
  }
  const { code, tests } = data as AreaErrorResponseData;

  switch (code) {
    case "AREA_FORBIDDEN":
      return t("area_error_forbidden", {
        defaultValue: "You do not have access to this instruction sphere.",
      });
    case "AREA_ARCHIVED":
      return t("area_error_archived", {
        defaultValue: "This instruction sphere is archived.",
      });
    case "AREA_GROUP_MISMATCH":
      return t("area_error_group_mismatch", {
        defaultValue: "This group belongs to a different instruction sphere.",
      });
    case "AREA_MISMATCH":
      return t("area_error_mismatch", {
        defaultValue: "This item belongs to a different instruction sphere.",
      });
    case "AREA_INCIDENTS_DISABLED":
      return t("area_error_incidents_disabled", {
        defaultValue: "This instruction sphere does not track incidents.",
      });
    case "AREA_SYSTEM_ARCHIVE_FORBIDDEN":
      return t("area_error_system_archive_forbidden", {
        defaultValue: "The system sphere cannot be archived.",
      });
    case "AREA_MOVE_HAS_TEST_LINKS": {
      const names = (tests || []).map((test) => test.name).filter(Boolean).join(", ");
      return t("area_error_move_has_test_links", {
        defaultValue:
          "This role is linked to training tests from another sphere ({{tests}}). Unlink them before moving the role.",
        tests: names,
      });
    }
    default:
      return null;
  }
}
